import React from "react";
import { HiExclamation, HiOutlineDocumentText } from "react-icons/hi";
import { FiTrash2 } from "react-icons/fi";
import { Modal } from "@components/common";
import type { ItemAfetado } from "@services/planilhaService";

interface Props {
  isOpen: boolean;
  itensAfetados: ItemAfetado[];
  onCancelar: () => void;
  onConfirmar: () => void;
  salvando?: boolean;
}

const formatarMoeda = (valor: number) =>
  valor.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

/** Rótulos amigáveis para os status que o backend devolve. */
const rotuloStatus: Record<string, string> = {
  respondida: "já respondeu",
  enviada: "está cotando",
  reenviada: "está cotando",
  rejeitada: "havia recusado",
  aguardando_envio: "ainda não enviado",
  aguardando: "ainda não enviado",
};

/**
 * Confirmação para alterações que invalidam cotações já solicitadas.
 *
 * O usuário precisa entender três coisas antes de decidir, e nesta ordem:
 * o que será apagado, de quem, e o que ele terá de fazer depois. A versão
 * anterior era um confirm() genérico com tudo concatenado em uma string, o que
 * escondia justamente o que está sendo perdido.
 */
export const ModalConfirmarInvalidacaoCotacoes: React.FC<Props> = ({
  isOpen,
  itensAfetados,
  onCancelar,
  onConfirmar,
  salvando = false,
}) => {
  const totalFornecedores = itensAfetados.reduce(
    (soma, item) => soma + item.fornecedoresAfetados.length,
    0,
  );
  const totalDocumentos = itensAfetados.reduce(
    (soma, item) =>
      soma + item.fornecedoresAfetados.filter((f) => f.temDocumento).length,
    0,
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onCancelar}
      title="Estas alterações apagam cotações já recebidas"
      size="large"
      footer={
        <>
          <button
            className="btn btn-ghost"
            onClick={onCancelar}
            disabled={salvando}
          >
            Cancelar e manter tudo
          </button>
          <button
            className="btn bg-red-600 text-white"
            onClick={onConfirmar}
            disabled={salvando}
          >
            {salvando ? (
              <>
                <span className="loading loading-spinner loading-sm" />
                Salvando…
              </>
            ) : (
              <>
                <FiTrash2 className="h-4 w-4" />
                Apagar e salvar alterações
              </>
            )}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        {/* O que vai acontecer, em uma frase */}
        <div className="alert alert-warning items-start">
          <HiExclamation className="h-6 w-6 shrink-0" />
          <div className="text-sm">
            <p className="font-semibold">
              Você alterou campos que mudam o que foi cotado.
            </p>
            <p className="mt-1">
              As propostas recebidas não valem mais para os novos dados. Ao
              continuar, os valores e os documentos serão{" "}
              <strong>apagados</strong> e{" "}
              <strong>todos os fornecedores</strong> das linhas abaixo voltam
              para "Solicitar cotação" — inclusive quem já havia recusado.
            </p>
          </div>
        </div>

        {/* Resumo numérico: dá a dimensão antes do detalhe */}
        <div className="flex flex-wrap gap-3">
          <div className="flex-1 min-w-[140px] rounded-lg border border-base-300 p-3">
            <span className="block text-xs uppercase tracking-wider text-base-content/60">
              Linhas afetadas
            </span>
            <span className="text-2xl font-bold">{itensAfetados.length}</span>
          </div>
          <div className="flex-1 min-w-[140px] rounded-lg border border-base-300 p-3">
            <span className="block text-xs uppercase tracking-wider text-base-content/60">
              Fornecedores a reenviar
            </span>
            <span className="text-2xl font-bold">{totalFornecedores}</span>
          </div>
          <div className="flex-1 min-w-[140px] rounded-lg border border-error/40 bg-error/5 p-3">
            <span className="block text-xs uppercase tracking-wider text-error">
              Documentos apagados
            </span>
            <span className="text-2xl font-bold text-error">
              {totalDocumentos}
            </span>
          </div>
        </div>

        {/* Detalhe por linha */}
        <div className="max-h-72 overflow-y-auto space-y-3 pr-1">
          {itensAfetados.map((item) => (
            <div
              key={item.itemId}
              className="rounded-lg border border-base-300 overflow-hidden"
            >
              <div className="bg-base-200 px-3 py-2">
                <p className="font-semibold text-sm">
                  {item.numero} — {item.item}
                </p>
                <p className="text-xs text-base-content/70 mt-0.5">
                  Alterado: {item.camposAlterados.join(", ")}
                </p>
              </div>

              <ul className="divide-y divide-base-200">
                {item.fornecedoresAfetados.map((f) => (
                  <li
                    key={f.fornecedorId}
                    className="flex items-center justify-between gap-3 px-3 py-2 text-sm"
                  >
                    <span className="min-w-0">
                      <span className="block truncate font-medium">
                        {f.fornecedorNome}
                      </span>
                      <span className="text-xs text-base-content/60">
                        {rotuloStatus[f.status] ?? f.status}
                      </span>
                    </span>

                    <span className="flex items-center gap-3 shrink-0">
                      {typeof f.valorCotado === "number" && (
                        <span className="text-error line-through">
                          {formatarMoeda(f.valorCotado)}
                        </span>
                      )}
                      {f.temDocumento && (
                        <span
                          className="flex items-center gap-1 text-xs text-error"
                          title="O documento assinado será apagado"
                        >
                          <HiOutlineDocumentText className="h-4 w-4" />
                          PDF
                        </span>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <p className="text-xs text-base-content/60">
          Esta ação não pode ser desfeita. Se quiser preservar as propostas
          atuais, cancele e desfaça a alteração nos campos.
        </p>
      </div>
    </Modal>
  );
};

export default ModalConfirmarInvalidacaoCotacoes;
